import React, { Component } from 'react';
import { Button, Modal, FormControl, FormGroup, ControlLabel } from 'react-bootstrap';
import { connect } from 'react-redux';

import OverlayLoader from '../components/overlayLoader';
import { showModalMessage } from '../store/modalsSlice';

import * as webViewerServersActions from '../store/webViewerServersSlice';
import { webViewerServersSelectors } from '../store/webViewerServersSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import ExpirationDateEdit from '../components/expirationDateEdit';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '../common/dateTimeFormats';

const typeMapping = {
  1: "personal",
  2: "concurrent",
  3: "unified",
};

const Mode = {
  Create: 0,
  Edit: 1,
};

const Titles = {
  [Mode.Create]: "Create new web viewer server",
  [Mode.Edit]: "Edit web viewer server",
};

const SubmitButtonCaptions = {
  [Mode.Create]: "Create",
  [Mode.Edit]: "Save",
};

const ConnectionSharingPolicy = {
  NoShare: 0,
  UseConnections: 1,
  UseLinks: 2,
};

const ConnectionSharingPolicyCaptions = {
  [ConnectionSharingPolicy.NoShare]: "Cannot use other types of connections",
  [ConnectionSharingPolicy.UseConnections]: "Connections by link can use users connections",
  [ConnectionSharingPolicy.UseLinks]: "Users connections can use connections by link",
};

const TemporaryLicensePeriodMonthCount = 12;

class WebViewerServerAddDialog extends Component {
  initState = {
    serverType: this.props.serverTypes.unified,
    term: null,
    startDate: null,
    connectionCount: 1,
    linkConnectionCount: 0,
    connectionSharingPolicy: ConnectionSharingPolicy.UseConnections,
    linkCount: 1000,
  };

  constructor(props) {
    super(props);
    this.handleClose = this.props.onClose;

    this.state = this.initState;

    this.onShow = this.onShow.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onServerTypeChange = this.onServerTypeChange.bind(this);
    this.onConnectionCountChange = this.onConnectionCountChange.bind(this);
    this.onConnectionSharingPolicyTypeChange = this.onConnectionSharingPolicyTypeChange.bind(this);
    this.onTermChanged = this.onTermChanged.bind(this);

    this.onSubmit = this.onSubmit.bind(this);
  }

  static get Mode() {
    return Mode;
  }

  onShow() {
    this.setState(this.initState);

    if (this.props.mode === Mode.Create) {
      const m = moment();

      this.setState({
        startDate: m,
        term: m.clone().add("months", TemporaryLicensePeriodMonthCount).diff(m, "days"),
      });
    }

    if (this.props.mode === Mode.Edit) {
      if (this.props.type !== undefined) {
        this.setState({ serverType: typeMapping[this.props.type] });
      }

      if (this.props.clientId !== undefined) {
        this.setState({ clientId: this.props.clientId });
      }

      if (this.props.connectionCount !== undefined) {
        this.setState({ connectionCount: this.props.connectionCount });
      }

      if (this.props.linkConnectionCount !== undefined) {
        this.setState({ linkConnectionCount: this.props.linkConnectionCount });
      }

      if (this.props.linkCount !== undefined) {
        this.setState({ linkCount: this.props.linkCount });
      }

      if (this.props.generationDate !== undefined) {
        this.setState({ startDate: moment(this.props.generationDate, DATE_TIME_FORMAT) });
      }

      if (this.props.term !== undefined) {
        this.setState({ term: this.props.term });
      }

      if (this.props.connectionSharingPolicy !== undefined) {
        this.setState({ connectionSharingPolicy: this.props.connectionSharingPolicy });
      }
    }
  }

  onClose() {
    this.handleClose();
    this.setState(this.initState);
  }

  isLoading() {
    return this.props.isWebViewerServerCreating;
  }

  getLoaderMessage() {
    if (this.props.isWebViewerServerCreating) {
      return "Web viewer server creating...";
    }
  }

  capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  isSubmitButtonDisabled() {
    return this.isLoading();
  }

  onServerTypeChange(e) {
    this.setState({ serverType: e.target.value });
  }

  onConnectionSharingPolicyTypeChange(e) {
    this.setState({ connectionSharingPolicy: e.target.value });
  }

  onTermChanged(value) {
    this.setState({ term: value });
  }

  onConnectionCountChange(e) {
    const count = e.target.value;
    const name = e.target.name;

    const re = /^\d+$/;

    if (count.length === 0 || !re.test(count)) {
      this.setState({ [name]: null });
      return;
    }
    this.setState({ [name]: parseInt(count, 10) });
  }

  onSubmit() {
    this.onClose();
    let submitPromise;

    const { serverType, connectionCount, term, linkConnectionCount, connectionSharingPolicy, linkCount } = this.state;
    const { clientId, serverId } = this.props;

    switch (this.props.mode) {
      case Mode.Create:
        submitPromise = this.props.addWebViewerServer({
          type: serverType,
          connectionCount,
          clientId,
          term,
          linkConnectionCount,
          connectionSharingPolicy,
          linkCount,
        });
        break;
      case Mode.Edit:
        submitPromise = this.props.editWebViewerServer({
          serverId,
          term,
          connectionCount,
          linkConnectionCount,
          connectionSharingPolicy,
          linkCount,
        });
        break;
      default:
        submitPromise = null;
    }

    if (submitPromise == null) {
      this.props.showModalMessage('Error', 'Unknown dialog mode');
      return;
    }

    return submitPromise
      .then(unwrapResult)
      .then(response => this.props.showModalMessage('Web viewer server was created', response.key))
      .catch((e) => {
        console.log(e);
        console.log(this.props.onSubmitError);
        this.props.showModalMessage("Error", this.props.onSubmitError.toString());
      });
  }

  renderUnlimitedIndicator = (value) => {
    if (value === "" || value === null) {
      return <span>unlimited</span>;
    }

    return "";
  }

  renderUnifiedSettings = () => (
    <React.Fragment>
      <FormGroup controlId="fgLicensePeriod">
        <ControlLabel>License period</ControlLabel>
        <ExpirationDateEdit
          startDate={this.state.startDate}
          term={this.state.term}
          onChange={this.onTermChanged}
          elementType="licensePeriod"
          enableUnlimited
        />
      </FormGroup>

      <FormGroup controlId="fcConnectionCount">
        <ControlLabel>Connection count</ControlLabel>
        <FormControl
          value={this.state.connectionCount}
          name="connectionCount"
          type="number"
          min={0}
          onChange={this.onConnectionCountChange}
        />
        {this.renderUnlimitedIndicator(this.state.connectionCount)}
      </FormGroup>

      <FormGroup controlId="fcLinkConnectionCount">
        <ControlLabel>Link connection count</ControlLabel>
        <FormControl
          value={this.state.linkConnectionCount}
          name="linkConnectionCount"
          type="number"
          min={0}
          onChange={this.onConnectionCountChange}
        />
        {this.renderUnlimitedIndicator(this.state.linkConnectionCount)}
      </FormGroup>

      <FormGroup controlId="fcConnectionSharingPolicy">
        <ControlLabel>Connection sharing policy</ControlLabel>
        <FormControl
          componentClass="select"
          value={this.state.connectionSharingPolicy}
          onChange={this.onConnectionSharingPolicyTypeChange}
        >
          {Object.values(ConnectionSharingPolicy).map((value) =>
            <option key={value} value={value}>
              {ConnectionSharingPolicyCaptions[value]}
            </option>)}
        </FormControl>
      </FormGroup>

      <FormGroup controlId="fcLinkCount">
        <ControlLabel>Link count</ControlLabel>
        <FormControl
          value={this.state.linkCount}
          name="linkCount"
          type="number"
          min={0}
          onChange={this.onConnectionCountChange} />
      </FormGroup>
    </React.Fragment>
  )

  render() {
    return (
      <Modal
        className="web-viewer-server-add-dialog"
        bsSize="small"
        show={this.props.show}
        onShow={this.onShow}
        onHide={this.onClose}
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>{Titles[this.props.mode]}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
            {this.props.clientId ? 
              <FormGroup controlId="fcClientName">
                <ControlLabel>Client name</ControlLabel>
                <FormControl.Static>{this.props.clientName}</FormControl.Static>
              </FormGroup> : null
            }

            {this.props.mode === Mode.Create &&
              <FormGroup controlId="fcServerType">
                <ControlLabel>Server type</ControlLabel>
                <FormControl
                  componentClass="select"
                  placeholder="Server type"
                  value={this.state.serverType}
                  onChange={this.onServerTypeChange}
                >
                  {Object.keys(this.props.serverTypes).map((serverType) => 
                    <option 
                      key={serverType}
                      value={serverType}
                    >
                      {this.capitalize(serverType)}
                    </option>)}
                </FormControl>
              </FormGroup>
            }
            {this.state.serverType === this.props.serverTypes.concurrent && 
              <FormGroup controlId="fcConnectionCount">
                <ControlLabel>Connection count</ControlLabel>
                <FormControl
                  value={this.state.connectionCount}
                  name="connectionCount"
                  type="number" 
                  min="1"
                  onChange={this.onConnectionCountChange} />
              </FormGroup>}

            {this.state.serverType === this.props.serverTypes.unified && this.renderUnifiedSettings()}
          </OverlayLoader>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.onSubmit} disabled={this.isSubmitButtonDisabled()}>
            {SubmitButtonCaptions[this.props.mode]}
          </Button>
          <Button onClick={this.onClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  isWebViewerServerCreating: webViewerServersSelectors.isCreating(state),
  onSubmitError: webViewerServersSelectors.creatingUpdatingError(state),
  serverTypes: webViewerServersSelectors.state(state).serverTypes,
});

const actionCreators = {
  addWebViewerServer: webViewerServersActions.create,
  editWebViewerServer: webViewerServersActions.update,
  showModalMessage: (title, text) => showModalMessage({ title, text }),
};

export default connect(mapStateToProps, actionCreators)(WebViewerServerAddDialog);



// WEBPACK FOOTER //
// ./src/containers/webViewerServerAddDialog.js